/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { mcpServersUpdate } from "../funcs/mcpServersUpdate.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { McpServer } from "../models/components/mcpserver.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  UpdateMcpServerRequest,
  UpdateMcpServerSecurity,
} from "../models/operations/updatemcpserver.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type UpdateMcpServerMutationVariables = {
  request: UpdateMcpServerRequest;
  security?: UpdateMcpServerSecurity | undefined;
  options?: RequestOptions;
};

export type UpdateMcpServerMutationData = McpServer;

export type UpdateMcpServerMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * updateMcpServer mcpServers
 *
 * @remarks
 * Update an MCP server. This is a full-record replace for the optional UUID references: fields omitted from the request become null on the stored record. name is an exception — omitting it leaves the existing display name unchanged, while providing it requires a non-empty value and recomputes the server-side slug. The id and visibility fields are required; exactly one of remote_mcp_server_id, tunneled_mcp_server_id, or toolset_id must be provided.
 */
export function useUpdateMcpServerMutation(
  options?: MutationHookOptions<
    UpdateMcpServerMutationData,
    UpdateMcpServerMutationError,
    UpdateMcpServerMutationVariables
  >,
): UseMutationResult<
  UpdateMcpServerMutationData,
  UpdateMcpServerMutationError,
  UpdateMcpServerMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildUpdateMcpServerMutation(client, options),
    ...options,
  });
}

export function mutationKeyUpdateMcpServer(): MutationKey {
  return ["@gram/client", "mcpServers", "update"];
}

export function buildUpdateMcpServerMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: UpdateMcpServerMutationVariables,
  ) => Promise<UpdateMcpServerMutationData>;
} {
  return {
    mutationKey: mutationKeyUpdateMcpServer(),
    mutationFn: function updateMcpServerMutationFn({
      request,
      security,
      options,
    }): Promise<UpdateMcpServerMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(mcpServersUpdate(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
